import {ChangeEvent, ReactElement, useRef, useState} from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography
} from '@mui/material';
import {TFunction, useTranslation} from 'react-i18next';
import useConfirmDialogContext from 'contexts/ConfirmDialogContext/useConfirmDialogContext';
import {ExtensionType} from 'interfaces/Project';

type Props = {
  deleteTrayFromSection: () => void;
  extensionType: ExtensionType;
  hasSelectedTray: boolean;
  initialExtensionValue: number | null;
  onValueChange: (value: number) => void;
  onExtensionTypeChange: (value: ExtensionType) => void;
  t: TFunction;
};

export default function SectionExtension({
  deleteTrayFromSection,
  extensionType,
  hasSelectedTray,
  initialExtensionValue,
  onValueChange,
  onExtensionTypeChange,
  t
}: Props): ReactElement {
  const {
    i18n: {language}
  } = useTranslation();
  const [value, setValue] = useState(`${initialExtensionValue}`);
  const [error, setError] = useState(false);
  const shouldShowConfirmDialog = useRef(hasSelectedTray);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openConfirmDialog = useConfirmDialogContext();

  function handleValueChange(event: ChangeEvent<HTMLInputElement>) {
    const number = parseFloat(event.target.value);
    const error = isNaN(number) || number < 0 || number > 100;

    setValue(event.target.value);
    setError(error);
    if (!error) {
      onValueChange(Number(event.target.value));
    }
  }

  function changeExtensionType(value: ExtensionType) {
    onExtensionTypeChange(value);
    if (value === ExtensionType.Link) {
      setValue('50');
      onValueChange(50);
    } else if (value === ExtensionType.Rush) {
      setValue('100');
      onValueChange(100);
    } else {
      setValue('10');
      onValueChange(10);
    }
  }

  function handleExtensionTypeChange(event: ChangeEvent<HTMLInputElement>) {
    if (shouldShowConfirmDialog.current) {
      event.preventDefault();
      openConfirmDialog({
        title: t('common:confirmResetTray.title'),
        text: t('common:confirmResetTray.text'),
        onConfirm: function () {
          shouldShowConfirmDialog.current = false;
          deleteTrayFromSection();
          changeExtensionType(event.target.value as ExtensionType);
          setTimeout(() => inputRef.current?.focus(), 0);
        },
        onCancel: function () {
          setValue(`${initialExtensionValue}`);
        }
      });
    } else {
      changeExtensionType(event.target.value as ExtensionType);
    }
  }

  function onMouseDown() {
    if (shouldShowConfirmDialog.current) {
      openConfirmDialog({
        title: t('common:confirmResetTray.title'),
        text: t('common:confirmResetTray.text'),
        onConfirm: function () {
          shouldShowConfirmDialog.current = false;
          setTimeout(() => inputRef.current?.focus(), 0);
          deleteTrayFromSection();
        },
        onCancel: function () {
          setValue(`${initialExtensionValue}`);
        }
      });
    }
  }

  function handleSliderChange(event: any, newValue: number | number[]) {
    setValue(`${newValue as number}`);
  }

  function handleSliderChangeCommited(event: any, newValue: number | number[]) {
    if (shouldShowConfirmDialog.current) {
      openConfirmDialog({
        title: t('common:confirmResetTray.title'),
        text: t('common:confirmResetTray.text'),
        onConfirm: function () {
          shouldShowConfirmDialog.current = false;
          deleteTrayFromSection();
          onValueChange(Number(newValue));
        },
        onCancel: function () {
          setValue(`${initialExtensionValue}`);
        }
      });
    } else {
      onValueChange(Number(newValue));
    }
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <FormControl fullWidth>
        <RadioGroup
          aria-labelledby="cable-extension-label"
          value={extensionType}
          name="radio-buttons-group"
          onChange={handleExtensionTypeChange}
        >
          <FormControlLabel
            value={ExtensionType.Normal}
            control={<Radio />}
            label={t('common:extensionType.normal')}
          />
          {language === 'es' && (
            <>
              <FormControlLabel
                value={ExtensionType.Rush}
                control={<Radio />}
                label={t('common:itcbt14')}
              />
              <FormControlLabel
                value={ExtensionType.Link}
                control={<Radio />}
                label={t('common:itcbt16')}
              />
            </>
          )}
        </RadioGroup>
      </FormControl>
      <Box>
        <TextField
          id="percentageExtension"
          label={t('common:percentageExtension.label')}
          helperText={
            error
              ? t('common:percentageExtension.error')
              : extensionType === ExtensionType.Normal
              ? t('common:percentageExtension.help')
              : null
          }
          error={error}
          value={value}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>
          }}
          onChange={handleValueChange}
          inputProps={{
            step: '5.0'
          }}
          type="number"
          fullWidth
          onMouseDown={onMouseDown}
          inputRef={inputRef}
          sx={{mb: 2}}
        />
        <Slider
          value={+value}
          min={0}
          max={100}
          step={10}
          marks
          aria-label={t('common:percentageExtension.label')}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderChangeCommited}
          sx={{
            color: 'grey.600',
            '& .MuiSlider-thumb': {
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(96, 96, 96, 0.16)'
              },
              '&.Mui-active': {
                boxShadow: '0 0 0 14px rgba(96, 96, 96, 0.16)'
              }
            }
          }}
        />
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Typography sx={{fontSize: 14, color: 'text.secondary'}}>0</Typography>
          <Typography sx={{fontSize: 14, color: 'text.secondary'}}>100</Typography>
        </Box>
      </Box>
    </Box>
  );
}
